'use client';

import { createContext, ReactNode, useContext, useRef } from 'react';
import { create, useStore } from 'zustand';
import { defaultLocale, Locale, locales } from '@ae/i18n';
import { useShallow } from 'zustand/react/shallow';
import { changeLocaleFn } from './utils';

export { Locale, locales, defaultLocale };

type LanguageStoreState = {
  locale: Locale;
  supportedLngs: Locale[];
};

type LanguageStoreActions = {
  changeLocale: (locale: Locale) => void;
};

type LanguageStoreType = LanguageStoreState & LanguageStoreActions;

const createLanguageStore = (initialValues: Partial<LanguageStoreType> = {}) =>
  create<LanguageStoreType>((set) => ({
    locale: initialValues.locale || defaultLocale,
    supportedLngs: initialValues.supportedLngs || [
      Locale.Nl,
      Locale.Fr,
      Locale.En,
      Locale.De,
    ],
    changeLocale: (locale: Locale) => {
      set({ locale });
      initialValues.changeLocale
        ? initialValues.changeLocale(locale)
        : changeLocaleFn(locale);
    },
  }));

const LanguageStoreContext = createContext<ReturnType<
  typeof createLanguageStore
> | null>(null);

export const LanguageProvider = ({
  children,
  locale,
  supportedLngs,
  changeLocale,
}: {
  children: ReactNode;
  locale?: Locale;
  supportedLngs?: Locale[];
  changeLocale?: (locale: Locale) => void;
}) => {
  const store = useRef<ReturnType<typeof createLanguageStore> | null>(null);

  if (!store.current) {
    store.current = createLanguageStore({
      locale,
      supportedLngs,
      changeLocale,
    });
  }

  return (
    <LanguageStoreContext.Provider value={store.current}>
      {children}
    </LanguageStoreContext.Provider>
  );
};

const useLanguageStore = <T,>(selector: (state: LanguageStoreType) => T): T => {
  const context = useContext(LanguageStoreContext);
  if (!context) {
    throw new Error('useLanguageStore must be used within a LanguageProvider');
  }
  return useStore(context, useShallow(selector));
};

export const useLocale = (): Locale =>
  useLanguageStore((state) => state.locale as Locale);
export const useSupportedLngs = (): string[] =>
  useLanguageStore((state) => state.supportedLngs);
export const useChangeLocale = (): ((locale: Locale) => void) =>
  useLanguageStore((state) => state.changeLocale);
